import React, { useMemo, useState } from 'react';
import { portalBausteinSurfaceClass } from '../portalAppearance';
import { usePortalAppearance } from '../portalAppearanceContext';
import { PORTAL_NAV_BAUSTEIN_LABEL } from '../portalNavigation';
import {
  bausteinBeschreibung,
  bausteinModulSatz,
  bausteinNaechsteZeile,
  bausteinSitzAnzeige,
  bausteinZeileAnfrage,
  bausteinZeigtBestellen,
  bausteinZeigtKuendigen,
  type BausteinAnzeigeZeile,
} from '../portalBausteinSitz';
import {
  BAUSTEIN_DOT_ON,
  BAUSTEIN_NAME_OFF,
  BAUSTEIN_NAME_ON,
  BAUSTEIN_STATUS_CLASS_OFF,
  BAUSTEIN_STATUS_CLASS_ON,
  BAUSTEIN_STATUS_OFF,
  BAUSTEIN_STATUS_ON,
  type BausteinSeatInput,
} from '../portalBausteinUnlock';

const BAUSTEIN_GROUP_BASIS = 'Basis';
const BAUSTEIN_GROUP_MODULE = 'Module';

/** Schwarzer Grund, feine lila Kante — wie die Akte. Volle Fläche, ruhige Zeilen. */
const BAUSTEIN_GROUND = 'bg-[#07070a]';
const BAUSTEIN_EDGE = 'rounded-lg border border-violet-400/30 bg-[#07070a]';
const BAUSTEIN_SHELL =
  `flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-y-auto ${BAUSTEIN_GROUND} ${BAUSTEIN_EDGE}`;
const BAUSTEIN_LI_ON = 'border-b border-violet-400/20';
const BAUSTEIN_LI_OFF = 'border-b border-violet-400/15';
const BAUSTEIN_ROW_ON = 'flex items-center gap-3 bg-transparent px-1 py-2';
const BAUSTEIN_ROW_OFF = 'flex items-center gap-3 bg-transparent px-1 py-2';

function Zeile({
  name,
  on,
  gruppe,
  satz,
  detail,
  detailOffen,
  bestellen,
  kuendigen,
  angefragt,
  schreibBusy,
  frage,
  onBestellen,
  onKuendigen,
  onFrage,
  onZustimmen,
  onAbbrechen,
  onOeffnen,
}: {
  name: string;
  on: boolean;
  gruppe: BausteinAnzeigeZeile['gruppe'];
  satz: string;
  detail: string;
  detailOffen: boolean;
  bestellen: boolean;
  kuendigen: boolean;
  angefragt: boolean;
  schreibBusy: boolean;
  frage: boolean;
  onBestellen?: (name: string) => void;
  onKuendigen?: (name: string, zugestimmt: boolean) => void;
  onFrage?: (name: string) => void;
  onZustimmen?: (name: string) => void;
  onAbbrechen?: () => void;
  onOeffnen?: (name: string) => void;
}) {
  const nameClass = `truncate ${on ? BAUSTEIN_NAME_ON : BAUSTEIN_NAME_OFF}`;
  const kopf = (
    <>
      <p className={nameClass}>{name}</p>
      {satz ? <p className="truncate text-sm text-slate-400">{satz}</p> : null}
    </>
  );
  return (
    <li
      className={on ? BAUSTEIN_LI_ON : BAUSTEIN_LI_OFF}
      data-baustein-name={name}
      data-baustein-gruppe={gruppe}
      data-baustein-unlock={on ? 'on' : 'off'}
    >
      <div className={on ? BAUSTEIN_ROW_ON : BAUSTEIN_ROW_OFF}>
        {on ? <span className={BAUSTEIN_DOT_ON} aria-hidden /> : <span className="mr-2 inline-block h-2.5 w-2.5 shrink-0" aria-hidden />}
        <div className="min-w-0 flex-1">
          {detail ? (
            <button
              type="button"
              className="block min-w-0 max-w-full text-left"
              data-baustein-oeffnen={name}
              aria-expanded={detailOffen}
              onClick={() => onOeffnen?.(name)}
            >
              {kopf}
            </button>
          ) : (
            kopf
          )}
        </div>
        <p className={`shrink-0 !mt-0 ${on && !angefragt ? 'baustein-frei-rahmen' : (angefragt || !on ? BAUSTEIN_STATUS_CLASS_OFF : BAUSTEIN_STATUS_CLASS_ON)}`}>
          {angefragt ? 'angefragt' : on ? BAUSTEIN_STATUS_ON : BAUSTEIN_STATUS_OFF}
        </p>
        {bestellen ? (
          <button
            type="button"
            className="shrink-0 rounded border border-violet-400/40 px-2 py-1 text-sm text-violet-100"
            data-baustein-bestellen={name}
            disabled={schreibBusy}
            onClick={() => onBestellen?.(name)}
          >
            Freischalten anfragen
          </button>
        ) : null}
        {kuendigen ? (
          <button
            type="button"
            className="shrink-0 rounded border border-violet-400/40 px-2 py-1 text-sm text-violet-100"
            data-baustein-kuendigen={name}
            disabled={schreibBusy}
            onClick={() => onFrage?.(name)}
          >
            Kündigung anfragen
          </button>
        ) : null}
      </div>
      {frage && kuendigen ? (
        <div className="ml-6 flex flex-wrap items-center gap-2 px-3 pb-3" data-baustein-bestaetigung={name}>
          <p className="text-sm text-slate-300">Die Anfrage merken. Die Freischaltung bleibt.</p>
          <button
            type="button"
            className="shrink-0 rounded border border-violet-400/40 px-2 py-1 text-sm text-violet-100"
            data-baustein-zustimmung={name}
            disabled={schreibBusy}
            onClick={() => onZustimmen?.(name)}
          >
            Zustimmen
          </button>
          <button
            type="button"
            className="shrink-0 rounded border border-violet-400/40 px-2 py-1 text-sm text-violet-100"
            data-baustein-abbrechen={name}
            onClick={() => onAbbrechen?.()}
          >
            Abbrechen
          </button>
        </div>
      ) : null}
      {detailOffen && detail ? (
        <p
          className="ml-6 border-l border-violet-400/40 px-3 pb-3 text-sm leading-relaxed text-slate-300"
          data-baustein-text={name}
        >
          {detail}
        </p>
      ) : null}
    </li>
  );
}

function Gruppe({
  id,
  title,
  titleClass,
  rows,
  seat,
  schreibName,
  frageName,
  onBestellen,
  onKuendigen,
  onFrage,
  onZustimmen,
  onAbbrechen,
  onOeffnen,
  offenName,
}: {
  id: string;
  title: string;
  titleClass: string;
  rows: BausteinAnzeigeZeile[];
  seat: BausteinSeatInput;
  schreibName: string;
  frageName: string;
  onBestellen?: (name: string) => void;
  onKuendigen?: (name: string, zugestimmt: boolean) => void;
  onFrage?: (name: string) => void;
  onZustimmen?: (name: string) => void;
  onAbbrechen?: () => void;
  onOeffnen?: (name: string) => void;
  offenName: string;
}) {
  return (
    <section className="mt-6" aria-labelledby={id}>
      <h2 id={id} className={titleClass}>
        {title}
      </h2>
      <ul className="mt-1">
        {rows.map((zeile) => (
          <Zeile
            key={zeile.name}
            name={zeile.name}
            on={zeile.offen}
            gruppe={zeile.gruppe}
            satz={zeile.gruppe === 'modul' ? bausteinModulSatz(zeile.name) : ''}
            detail={bausteinBeschreibung(zeile.name)}
            detailOffen={offenName === zeile.name}
            bestellen={bausteinZeigtBestellen(zeile.gruppe, zeile.offen, seat, zeile.name)}
            kuendigen={bausteinZeigtKuendigen(zeile.gruppe, zeile.offen, seat, zeile.name)}
            angefragt={bausteinZeileAnfrage(zeile.name, seat)}
            schreibBusy={schreibName === zeile.name}
            frage={frageName === zeile.name}
            onBestellen={onBestellen}
            onKuendigen={onKuendigen}
            onFrage={onFrage}
            onZustimmen={onZustimmen}
            onAbbrechen={onAbbrechen}
            onOeffnen={onOeffnen}
          />
        ))}
      </ul>
    </section>
  );
}

export const BausteinView: React.FC<{
  seat?: BausteinSeatInput;
  onBestellen?: (name: string) => void;
  onKuendigen?: (name: string, zugestimmt: boolean) => void;
  bestellName?: string;
  bestellHinweis?: string;
}> = ({ seat = {}, onBestellen, onKuendigen, bestellName = '', bestellHinweis = '' }) => {
  const rows = useMemo(() => bausteinSitzAnzeige(seat), [seat]);
  const basis = rows.filter((zeile) => zeile.gruppe === 'basis');
  const modulZeilen = rows.filter((zeile) => zeile.gruppe === 'modul');
  const bausteinLight = portalBausteinSurfaceClass(usePortalAppearance());
  const [offenName, setOffenName] = useState('');
  const [frageName, setFrageName] = useState('');
  const oeffnen = (name: string) => {
    setOffenName((cur) => bausteinNaechsteZeile(cur, name));
  };
  const fragen = (name: string) => {
    if (bausteinZeileAnfrage(name, seat)) return;
    setFrageName(name);
  };
  const abbrechen = () => {
    setFrageName('');
  };
  const zustimmen = (name: string) => {
    setFrageName('');
    if (bausteinZeileAnfrage(name, seat)) return;
    onKuendigen?.(name, true);
  };

  return (
    <div data-testid="portal-baustein-pane" className={`${BAUSTEIN_SHELL} px-4 py-5 sm:px-6 ${bausteinLight}`}>
      <h1 className="text-2xl font-bold text-white">{PORTAL_NAV_BAUSTEIN_LABEL}</h1>
      <p className="mt-1 text-slate-400">Was freigeschaltet ist, und was noch dazukommen kann.</p>
      {bestellHinweis ? <p className="mt-2 text-sm text-slate-400">{bestellHinweis}</p> : null}
      <Gruppe
        id="baustein-basis"
        title={BAUSTEIN_GROUP_BASIS}
        titleClass="text-sm font-semibold text-violet-100"
        rows={basis}
        seat={seat}
        schreibName={bestellName}
        frageName={frageName}
        onBestellen={onBestellen}
        onKuendigen={onKuendigen}
        onFrage={fragen}
        onZustimmen={zustimmen}
        onAbbrechen={abbrechen}
        onOeffnen={oeffnen}
        offenName={offenName}
      />
      <Gruppe
        id="baustein-module"
        title={BAUSTEIN_GROUP_MODULE}
        titleClass="text-sm font-semibold text-slate-300"
        rows={modulZeilen}
        seat={seat}
        schreibName={bestellName}
        frageName={frageName}
        onBestellen={onBestellen}
        onKuendigen={onKuendigen}
        onFrage={fragen}
        onZustimmen={zustimmen}
        onAbbrechen={abbrechen}
        onOeffnen={oeffnen}
        offenName={offenName}
      />
    </div>
  );
};
